<template>
  <v-form v-model="valid" lazy-validation class="pa-4">
    <v-card variant="flat" class="mb-4">
      <v-card-title class="px-0 d-flex align-center">
        <v-icon icon="mdi-account-edit" color="primary" class="mr-2" />
        {{ user_edit.id ? "Gebruiker bewerken" : "Nieuwe gebruiker" }}
      </v-card-title>
    </v-card>

    <v-row>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="user_edit.first_name"
          label="Voornaam"
          placeholder="Voornaam"
          :rules="[rules.required]"
          variant="outlined"
          density="comfortable"
          prepend-inner-icon="mdi-account"
          class="mb-2"
        />
      </v-col>

      <v-col cols="12" sm="6">
        <v-text-field
          v-model="user_edit.last_name"
          label="Achternaam"
          placeholder="Achternaam"
          :rules="[rules.required]"
          variant="outlined"
          density="comfortable"
          prepend-inner-icon="mdi-account-box"
          class="mb-2"
        />
      </v-col>
    </v-row>

    <v-text-field
      v-model="user_edit.email"
      label="Email"
      placeholder="Email"
      :rules="[rules.required, rules.email]"
      variant="outlined"
      density="comfortable"
      prepend-inner-icon="mdi-email"
      class="mb-4"
    />

    <v-select
      v-model="user_edit.roles"
      :items="roles"
      label="Rollen"
      :rules="[rules.roleSelect]"
      multiple
      variant="outlined"
      density="comfortable"
      prepend-inner-icon="mdi-shield-account"
      chips
      hint="Kies de gewenste rollen. beta_tester (AI-assistent) geldt alleen voor deze organisatie."
      persistent-hint
      class="mb-4"
    >
      <template #selection="{ attrs, item, parent, selected }">
        <v-chip
          v-bind="attrs"
          color="primary"
          :input-value="selected"
          label
          size="small"
          class="mr-1"
        >
          <v-icon start size="small">mdi-shield-check</v-icon>
          {{ item }}
          <v-btn
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop.prevent="parent.selectItem(item)"
          />
        </v-chip>
      </template>
    </v-select>

    <!-- Tenant selection -->
    <v-select
      v-model="user_edit.tenant_ids"
      :items="tenantsStore.tenantOptions"
      label="Tenants"
      multiple
      variant="outlined"
      density="comfortable"
      prepend-inner-icon="mdi-domain"
      chips
      hint="Kies de tenants waar deze gebruiker toegang toe heeft"
      persistent-hint
      class="mb-4"
      :loading="tenantsStore.isLoading"
    >
      <template #selection="{ attrs, item, parent, selected }">
        <v-chip
          v-bind="attrs"
          color="info"
          :input-value="selected"
          label
          size="small"
          class="mr-1"
        >
          <v-icon start size="small">mdi-domain</v-icon>
          {{ item.title }}
          <v-btn
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop.prevent="parent.selectItem(item)"
          />
        </v-chip>
      </template>
    </v-select>

    <v-expand-transition>
      <div v-if="user_edit.id == null" class="mb-4">
        <v-divider class="mb-4" />
        <v-text-field
          v-model="user_edit.password"
          label="Wachtwoord"
          placeholder="Wachtwoord"
          :type="show_pw ? 'text' : 'password'"
          :append-icon="show_pw ? 'mdi-eye' : 'mdi-eye-off'"
          :rules="[rules.required, rules.minsize]"
          variant="outlined"
          density="comfortable"
          prepend-inner-icon="mdi-lock"
          class="mb-2"
          @click:append="show_pw = !show_pw"
        />
        <v-text-field
          v-model="user_edit.repeat_password"
          label="Herhaal wachtwoord"
          placeholder="Herhaal wachtwoord"
          :type="show_pw ? 'text' : 'password'"
          :append-icon="show_pw ? 'mdi-eye' : 'mdi-eye-off'"
          :rules="[rules.required, rules.minsize]"
          variant="outlined"
          density="comfortable"
          prepend-inner-icon="mdi-lock-check"
          @click:append="show_pw = !show_pw"
        />
      </div>
    </v-expand-transition>

    <v-card-actions class="pa-0">
      <v-spacer />
      <v-btn
        color="primary"
        size="large"
        :loading="loading"
        :disabled="!valid"
        prepend-icon="mdi-content-save"
        @click="submit"
      >
        {{ user_edit.id ? "Bijwerken" : "Aanmaken" }}
      </v-btn>
    </v-card-actions>
  </v-form>
</template>

<script>
import { useFormsStore } from "@/stores/formsStore";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import { useTenantsStoreRefactor } from "@/stores/tenantsStoreRefactor";
import { useAlertStore } from "@/stores/alertStore";

export default {
  name: "UserForm",
  props: ["user", "popup"],
  setup() {
    const formsStore = useFormsStore();
    const usersStore = useUsersStoreRefactor();
    const tenantsStore = useTenantsStoreRefactor();
    const alertStore = useAlertStore();
    return { formsStore, usersStore, tenantsStore, alertStore };
  },
  data() {
    return {
      user_edit: {
        first_name: "",
        last_name: "",
        email: "",
        roles: [],
        tenant_ids: [],
        password: "",
        repeat_password: "",
      },
      valid: true,
      loading: false,
      show_pw: false,
      rules: {
        required: (value) => !!value || "Dit veld is verplicht.",
        minsize: (value) =>
          !value || value.length >= 8 || "Minimaal 8 karakters",
        email: (v) => /.+@.+\..+/.test(v) || "Geen geldige E-mail",
        roleSelect: (v) => v.length > 0 || "Selecteer minimaal 1 rol",
      },
    };
  },
  computed: {
    roles() {
      return [
        "admin",
        "planner",
        "member",
        "notification",
        "report",
        "read_only",
        "job_scheduler",
        // AI assistant access; granted for the current organisation only
        "beta_tester",
      ];
    },
  },
  async mounted() {
    // Ensure tenants are loaded (force refresh to be safe)
    try {
      await this.tenantsStore.fetchAll({}, true);
    } catch (e) {
      // ignore
    }

    this.user_edit = {
      ...this.user_edit,
      ...this.user,
      roles: this.user?.roles || [],
      tenant_ids: this.user?.tenant_ids || [],
    };
  },
  methods: {
    async submit() {
      if (this.valid) {
        this.loading = true;
        try {
          const action = this.user_edit.id
            ? this.usersStore.update
            : this.usersStore.create;
          await action(this.user_edit);
          await this.usersStore.fetchAll(); // Refresh the users list

          this.alertStore.setAlert(
            `Gebruiker succesvol ${this.user_edit.id ? "bijgewerkt" : "aangemaakt"}`,
            "success",
          );

          if (this.popup) {
            this.formsStore.$reset();
          }
        } catch (error) {
          // The store now handles error display with specific API messages
          // so we don't need to show a generic error here
          console.error("Error saving user:", error);
        } finally {
          this.loading = false;
        }
      }
    },
  },
};
</script>
